import { BookData, BookCoverData } from '../interfaces/book-data.interface';
import { DrawableData, isDrawableGroup } from '../interfaces/drawable-data.interface';
import { SpreadData } from '../interfaces/spread-data.interface';

export function setCover(book: BookData, cover: BookCoverData): BookData {
  return { ...book, cover };
}

export function setSpreads(book: BookData, spreads: readonly SpreadData[]): BookData {
  return { ...book, spreads };
}

// Full depth, matching updateDrawableRect's applyUpdate: PSD layer groups nest, and stopping one level short
// skipped the update silently instead of failing.
function applyToDrawable(d: DrawableData, drawableId: string, updater: (d: DrawableData) => DrawableData): DrawableData {
  if (d.id === drawableId) return updater(d);
  if (!isDrawableGroup(d)) return d;
  return { ...d, drawableObjects: d.drawableObjects.map(nested => applyToDrawable(nested, drawableId, updater)) };
}

/** Immutably update a spread (or cover) in a BookData by spread id. */
export function updateSpreadInBook(book: BookData, spreadId: number, updater: (s: SpreadData) => SpreadData): BookData {
  if (book.cover?.id === spreadId) {
    return { ...book, cover: updater(book.cover) as BookCoverData };
  }

  return {
    ...book,
    spreads: book.spreads.map(s => (s.id === spreadId ? updater(s) : s)),
  };
}

/** Immutably update a single drawable in a spread, identified by id. Handles nested drawables inside groups. */
export function updateDrawableInSpread(
  spread: SpreadData,
  drawableId: string,
  updater: (d: DrawableData) => DrawableData,
): SpreadData {
  return {
    ...spread,
    collages: spread.collages.map(collage => ({
      ...collage,
      drawableObjects: collage.drawableObjects.map(d => applyToDrawable(d, drawableId, updater)),
    })),
  };
}

/** Maps in place-identity: returns the original array when every item came back unchanged. */
function mapPreservingIdentity<T>(items: readonly T[], fn: (item: T) => T): readonly T[] {
  const mapped = items.map(fn);
  return mapped.every((item, index) => item === items[index]) ? items : mapped;
}

/** Drops the `editedByClient` text stamp, groups included — a nested PSD layer carries it just as well. */
function stripEditedByClient(drawable: DrawableData): DrawableData {
  let next = drawable;
  if (isDrawableGroup(next)) {
    const nested = mapPreservingIdentity(next.drawableObjects, stripEditedByClient);
    if (nested !== next.drawableObjects) next = { ...next, drawableObjects: nested };
  }

  if ((next as { editedByClient?: boolean }).editedByClient === undefined) return next;
  const { editedByClient: _textStamp, ...rest } = next as DrawableData & { editedByClient?: boolean };
  return rest as DrawableData;
}

/**
 * Removes the `addedByClient` stamp from every spread (cover included). MANDATORY at the entry of any
 * path that copies spreads FROM one album INTO another (sync, clone-from-master): the stamp carries the
 * student's right to DELETE a spread in their OWN album (FKS-10860) and, since FKS-8507, its DEFAULT lock
 * mode too — a stamped spread defaults to a free layout instead of the pinned one. A photographer syncing
 * from a student's book must hand out neither with the copied content.
 *
 * The `editedByClient` text stamp goes the same way: it makes a text outlive every rebuild and every sync
 * bucket, and that right does not travel into somebody else's album either.
 */
export function stripClientProvenance(book: BookData): BookData {
  const strip = <T extends SpreadData>(spread: T): T => {
    const collages = spread.collages
      ? mapPreservingIdentity(spread.collages, collage => {
          const drawableObjects = mapPreservingIdentity(collage.drawableObjects, stripEditedByClient);
          return drawableObjects === collage.drawableObjects ? collage : { ...collage, drawableObjects };
        })
      : spread.collages;
    const clean = collages === spread.collages ? spread : ({ ...spread, collages } as T);

    if (clean.addedByClient === undefined) return clean;
    const { addedByClient: _clientStamp, ...rest } = clean;
    return rest as T;
  };

  const spreads = book.spreads.map(strip);
  const cover = book.cover ? strip(book.cover) : book.cover;
  if (cover === book.cover && spreads.every((s, i) => s === book.spreads[i])) return book;

  return { ...book, spreads, cover };
}
